import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  Package, 
  Plus, 
  RefreshCw, 
  AlertTriangle, 
  CheckCircle2, 
  Settings,
  Pill,
  Droplet,
  X
} from 'lucide-react';
import { Button } from '../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tipi di unità disponibili
const UNITA_OPTIONS = [
  { value: 'compresse', label: 'Compresse', icon: '💊' },
  { value: 'ml', label: 'Millilitri (ml)', icon: '💧' },
  { value: 'gocce', label: 'Gocce', icon: '💦' },
  { value: 'bustine', label: 'Bustine', icon: '📦' }
];

// Progress bar colorata in base allo stato
const StockProgressBar = ({ current, max, threshold }) => {
  if (current === null || max === null) return null;
  
  const percentage = Math.min(100, (current / max) * 100);
  
  let colorClass = 'bg-[#4CAF50]'; // Verde
  if (current <= threshold) {
    colorClass = current <= 0 ? 'bg-[#EF4444]' : 'bg-[#F59E0B]'; // Rosso o Arancione
  }
  
  return (
    <div className="w-full h-2.5 bg-gray-200 rounded-full overflow-hidden">
      <div 
        className={`h-full ${colorClass} rounded-full transition-all duration-500`}
        style={{ width: `${percentage}%` }}
      />
    </div>
  );
};

// Card singolo farmaco con scorte
const StockCard = ({ medicine, onRefill, onConfigure }) => {
  const { 
    nome_farmaco, 
    dosaggio, 
    tipo_unita = 'compresse',
    quantita_confezione,
    scorta_attuale, 
    soglia_alert = 5,
    stato 
  } = medicine;
  
  const unitIcon = UNITA_OPTIONS.find(u => u.value === tipo_unita)?.icon || '💊';
  
  const getStatoBadge = () => {
    switch (stato) {
      case 'esaurito':
        return (
          <span className="px-2 py-1 rounded-full text-xs font-bold bg-red-100 text-red-700 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> Esaurito
          </span>
        );
      case 'basso':
        return (
          <span className="px-2 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-700 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> Scorta bassa
          </span>
        );
      case 'ok':
        return (
          <span className="px-2 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700 flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" /> OK
          </span>
        );
      default:
        return (
          <span className="px-2 py-1 rounded-full text-xs font-bold bg-gray-100 text-gray-600">
            Non configurato
          </span>
        );
    }
  };
  
  const isConfigured = scorta_attuale !== null && quantita_confezione !== null;
  
  return (
    <div className={`bg-white rounded-2xl border p-4 transition-all ${
      stato === 'basso' || stato === 'esaurito' 
        ? 'border-amber-300 shadow-amber-100 shadow-md' 
        : 'border-[#E5E7EB]'
    }`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl ${
            stato === 'esaurito' ? 'bg-red-100' :
            stato === 'basso' ? 'bg-amber-100' : 'bg-[#D5F5E3]'
          }`}>
            {unitIcon}
          </div>
          <div>
            <h3 className="font-semibold text-[#2C3E50]">{nome_farmaco}</h3>
            {dosaggio && <p className="text-sm text-[#64748B]">{dosaggio}</p>}
          </div>
        </div>
        {getStatoBadge()}
      </div>
      
      {isConfigured ? (
        <>
          {/* Scorta attuale */}
          <div className="mb-3">
            <div className="flex items-baseline justify-between mb-1">
              <span className="text-sm text-[#64748B]">Scorta attuale</span>
              <span className="text-lg font-bold text-[#2C3E50]">
                {scorta_attuale?.toFixed(1)} / {quantita_confezione} {tipo_unita}
              </span>
            </div>
            <StockProgressBar 
              current={scorta_attuale} 
              max={quantita_confezione} 
              threshold={soglia_alert} 
            />
          </div>
          
          {/* Info soglia */}
          <p className="text-xs text-[#9CA3AF] mb-3">
            Alert quando sotto {soglia_alert} {tipo_unita}
          </p>
          
          {/* Azioni */}
          <div className="flex gap-2">
            <Button
              onClick={() => onRefill(medicine)}
              size="sm"
              className="flex-1 bg-[#4A7C59] hover:bg-[#3A6345] text-white"
            >
              <Plus className="w-4 h-4 mr-1" />
              Ricarica
            </Button>
            <Button
              onClick={() => onConfigure(medicine)}
              size="sm"
              variant="outline"
              className="border-[#E5E7EB]"
            >
              <Settings className="w-4 h-4" />
            </Button>
          </div>
        </>
      ) : (
        /* Non configurato */
        <div className="text-center py-4">
          <p className="text-sm text-[#64748B] mb-3">
            Scorte non configurate per questo farmaco
          </p>
          <Button
            onClick={() => onConfigure(medicine)}
            size="sm"
            className="bg-[#4A7C59] hover:bg-[#3A6345] text-white"
          >
            <Settings className="w-4 h-4 mr-2" />
            Configura Scorte
          </Button>
        </div>
      )}
    </div>
  );
};

// Modal configurazione scorte
const ConfigureStockModal = ({ isOpen, onClose, medicine, onSave }) => {
  const [formData, setFormData] = useState({
    tipo_unita: 'compresse',
    quantita_confezione: 30,
    scorta_iniziale: 30,
    dose_standard: 1,
    soglia_alert: 7
  });
  const [saving, setSaving] = useState(false);
  
  useEffect(() => {
    if (medicine) {
      setFormData({
        tipo_unita: medicine.tipo_unita || 'compresse',
        quantita_confezione: medicine.quantita_confezione || 30,
        scorta_iniziale: medicine.scorta_attuale || medicine.quantita_confezione || 30,
        dose_standard: medicine.dose_standard || 1,
        soglia_alert: medicine.soglia_alert || 7
      });
    }
  }, [medicine]);
  
  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/medicine/stock/setup/${medicine.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        toast.success('Scorte configurate!');
        onSave();
        onClose();
      } else {
        toast.error('Errore nel salvataggio');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setSaving(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-md p-6 shadow-xl">
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full"
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>
        
        <h2 className="text-xl font-bold text-[#2C3E50] mb-1">Configura Scorte</h2>
        <p className="text-sm text-[#64748B] mb-4">{medicine?.nome_farmaco}</p>
        
        <div className="space-y-4">
          {/* Tipo unità */}
          <div>
            <label className="block text-sm font-medium text-[#64748B] mb-1.5">
              Tipo di unità
            </label>
            <div className="grid grid-cols-2 gap-2">
              {UNITA_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setFormData({...formData, tipo_unita: opt.value})}
                  className={`p-3 rounded-xl border-2 text-left transition-all ${
                    formData.tipo_unita === opt.value
                      ? 'border-[#4A7C59] bg-[#4A7C59]/5'
                      : 'border-[#E5E7EB] hover:border-[#4A7C59]/50'
                  }`}
                >
                  <span className="text-xl mr-2">{opt.icon}</span>
                  <span className="text-sm font-medium">{opt.label}</span>
                </button>
              ))}
            </div>
          </div>
          
          {/* Quantità confezione */}
          <div>
            <label className="block text-sm font-medium text-[#64748B] mb-1.5">
              Quantità per confezione
            </label>
            <input
              type="number"
              value={formData.quantita_confezione === 0 ? '' : formData.quantita_confezione}
              onChange={(e) => setFormData({
                ...formData, 
                quantita_confezione: e.target.value === '' ? 0 : parseFloat(e.target.value),
                scorta_iniziale: e.target.value === '' ? 0 : parseFloat(e.target.value)
              })}
              className="w-full px-4 py-2.5 rounded-xl border border-[#E5E7EB] focus:border-[#4A7C59] outline-none"
              placeholder="es. 30"
            />
          </div>
          
          {/* Scorta iniziale */}
          <div>
            <label className="block text-sm font-medium text-[#64748B] mb-1.5">
              Scorta attuale
            </label>
            <input
              type="number"
              step="0.5"
              value={formData.scorta_iniziale === 0 ? '' : formData.scorta_iniziale}
              onChange={(e) => setFormData({...formData, scorta_iniziale: e.target.value === '' ? 0 : parseFloat(e.target.value)})}
              className="w-full px-4 py-2.5 rounded-xl border border-[#E5E7EB] focus:border-[#4A7C59] outline-none"
              placeholder="Quante ne hai ora?"
            />
          </div>
          
          {/* Dose standard */}
          <div>
            <label className="block text-sm font-medium text-[#64748B] mb-1.5">
              Dose standard (per somministrazione)
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                step="0.5"
                value={formData.dose_standard}
                onChange={(e) => setFormData({...formData, dose_standard: parseFloat(e.target.value) || 1})}
                className="flex-1 px-4 py-2.5 rounded-xl border border-[#E5E7EB] focus:border-[#4A7C59] outline-none"
              />
              <span className="text-sm text-[#64748B]">{formData.tipo_unita}</span>
            </div>
          </div>
          
          {/* Soglia alert */}
          <div>
            <label className="block text-sm font-medium text-[#64748B] mb-1.5">
              Notifica quando sotto
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={formData.soglia_alert}
                onChange={(e) => setFormData({...formData, soglia_alert: parseFloat(e.target.value) || 5})}
                className="flex-1 px-4 py-2.5 rounded-xl border border-[#E5E7EB] focus:border-[#4A7C59] outline-none"
              />
              <span className="text-sm text-[#64748B]">{formData.tipo_unita}</span>
            </div>
          </div>
        </div>
        
        <div className="flex gap-3 mt-6">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 bg-[#4A7C59] hover:bg-[#3A6345] text-white"
          >
            {saving ? 'Salvataggio...' : 'Salva'}
          </Button>
          <Button onClick={onClose} variant="outline" className="border-[#E5E7EB]">
            Annulla
          </Button>
        </div>
      </div>
    </div>
  );
};

// Modal ricarica scorte
const RefillModal = ({ isOpen, onClose, medicine, onSave }) => {
  const [quantita, setQuantita] = useState(medicine?.quantita_confezione || 30);
  const [saving, setSaving] = useState(false);
  
  useEffect(() => {
    if (medicine) {
      setQuantita(medicine.quantita_confezione || 30);
    }
  }, [medicine]);
  
  const handleRefill = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/medicine/stock/${medicine.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          medicine_id: medicine.id,
          quantita_aggiunta: quantita
        })
      });
      
      if (response.ok) {
        toast.success(`Aggiunte ${quantita} ${medicine.tipo_unita}!`);
        onSave();
        onClose();
      } else {
        toast.error('Errore nel salvataggio');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setSaving(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-sm p-6 shadow-xl">
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full"
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>
        
        <div className="text-center mb-4">
          <div className="w-16 h-16 bg-[#D5F5E3] rounded-2xl flex items-center justify-center mx-auto mb-3">
            <Plus className="w-8 h-8 text-[#4A7C59]" />
          </div>
          <h2 className="text-xl font-bold text-[#2C3E50]">Ricarica Scorte</h2>
          <p className="text-sm text-[#64748B]">{medicine?.nome_farmaco}</p>
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-[#64748B] mb-1.5">
            Quantità da aggiungere
          </label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              value={quantita === 0 ? '' : quantita}
              onChange={(e) => setQuantita(e.target.value === '' ? 0 : parseFloat(e.target.value))}
              className="flex-1 px-4 py-3 rounded-xl border border-[#E5E7EB] focus:border-[#4A7C59] outline-none text-lg font-semibold text-center"
            />
            <span className="text-[#64748B]">{medicine?.tipo_unita}</span>
          </div>
          
          {/* Quick buttons */}
          <div className="flex gap-2 mt-2">
            {[10, 20, 30, 60].map(n => (
              <button
                key={n}
                onClick={() => setQuantita(n)}
                className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
                  quantita === n 
                    ? 'bg-[#4A7C59] text-white' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                +{n}
              </button>
            ))}
          </div>
        </div>
        
        <p className="text-sm text-[#64748B] text-center mb-4">
          Scorta attuale: <strong>{medicine?.scorta_attuale?.toFixed(1)}</strong> → 
          Nuova: <strong>{((medicine?.scorta_attuale || 0) + quantita).toFixed(1)}</strong> {medicine?.tipo_unita}
        </p>
        
        <Button
          onClick={handleRefill}
          disabled={saving || quantita <= 0}
          className="w-full bg-[#4A7C59] hover:bg-[#3A6345] text-white py-3"
        >
          {saving ? 'Salvataggio...' : 'Conferma Ricarica'}
        </Button>
      </div>
    </div>
  );
};

// Componente principale ScorteTab
const ScorteTab = ({ famiglia }) => {
  const [stock, setStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lowStockCount, setLowStockCount] = useState(0);
  
  // Modals
  const [configureModal, setConfigureModal] = useState({ open: false, medicine: null });
  const [refillModal, setRefillModal] = useState({ open: false, medicine: null });
  
  useEffect(() => {
    loadStock();
  }, [famiglia?.id]);
  
  const loadStock = async () => {
    if (!famiglia?.id) return;
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/medicine/stock/${famiglia.id}`);
      if (response.ok) {
        const data = await response.json();
        setStock(data.stock || []);
        setLowStockCount(data.low_stock_count || 0);
      }
    } catch (error) {
      console.error('Error loading stock:', error);
      toast.error('Errore nel caricamento scorte');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="space-y-6">
      {/* Header con alert */}
      {lowStockCount > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <p className="font-semibold text-amber-800">
              {lowStockCount} farmac{lowStockCount === 1 ? 'o' : 'i'} in esaurimento
            </p>
            <p className="text-sm text-amber-600">
              Ricordati di fare scorta!
            </p>
          </div>
        </div>
      )}
      
      {/* Lista scorte */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 text-[#4A7C59] animate-spin" />
        </div>
      ) : stock.length === 0 ? (
        <div className="text-center py-12 bg-[#FAF9F6] rounded-2xl">
          <Package className="w-12 h-12 mx-auto text-[#C8D6C5] mb-3" />
          <p className="text-[#64748B] mb-1">Nessun farmaco configurato</p>
          <p className="text-sm text-[#9CA3AF]">
            Aggiungi farmaci nella tab "Programma" per gestire le scorte
          </p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {stock.map(medicine => (
            <StockCard
              key={medicine.id}
              medicine={medicine}
              onRefill={(med) => setRefillModal({ open: true, medicine: med })}
              onConfigure={(med) => setConfigureModal({ open: true, medicine: med })}
            />
          ))}
        </div>
      )}
      
      {/* Info box */}
      <div className="bg-[#EBF5FB] rounded-2xl p-4 border border-[#5DADE2]/20">
        <div className="flex gap-3">
          <div className="w-8 h-8 bg-[#5DADE2]/20 rounded-lg flex items-center justify-center flex-shrink-0">
            <Package className="w-4 h-4 text-[#5DADE2]" />
          </div>
          <div>
            <p className="font-medium text-[#2471A3] text-sm">Come funziona?</p>
            <p className="text-sm text-[#5DADE2] mt-1">
              Quando l'assistente segna un farmaco come dato, la scorta si scala automaticamente. 
              Riceverai una notifica push quando la scorta scende sotto la soglia impostata.
            </p>
          </div>
        </div>
      </div>
      
      {/* Modals */}
      <ConfigureStockModal
        isOpen={configureModal.open}
        onClose={() => setConfigureModal({ open: false, medicine: null })}
        medicine={configureModal.medicine}
        onSave={loadStock}
      />
      
      <RefillModal
        isOpen={refillModal.open}
        onClose={() => setRefillModal({ open: false, medicine: null })}
        medicine={refillModal.medicine}
        onSave={loadStock}
      />
    </div>
  );
};

export default ScorteTab;
